/**
 * The interface's icons: flat, 24×24 line drawings that take the text colour. Each is a stroked
 * path and/or a filled one, rendered by Icon.svelte.
 */

/** A circle as path data, so every icon is plain paths. */
function circle(cx: number, cy: number, r: number): string {
  return `M${cx - r} ${cy}a${r} ${r} 0 1 0 ${2 * r} 0a${r} ${r} 0 1 0 ${-2 * r} 0z`;
}

const HEART =
  'M12 20s-7.5-4.6-7.5-10.2A4.3 4.3 0 0 1 12 7.2a4.3 4.3 0 0 1 7.5 2.6C19.5 15.4 12 20 12 20z';
const EYE = 'M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z';

export const ICONS = {
  close: { stroke: 'M18 6 6 18M6 6l12 12' },
  plus: { stroke: 'M12 5v14M5 12h14' },
  heart: { stroke: HEART },
  'heart-filled': { stroke: HEART, fill: HEART },
  dice: {
    stroke: 'M7 3h10a4 4 0 0 1 4 4v10a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V7a4 4 0 0 1 4-4z',
    fill: [
      circle(8.5, 8.5, 1.4),
      circle(15.5, 8.5, 1.4),
      circle(12, 12, 1.4),
      circle(8.5, 15.5, 1.4),
      circle(15.5, 15.5, 1.4),
    ].join(''),
  },
  eye: { stroke: EYE + circle(12, 12, 3) },
  'eye-off': { stroke: EYE + circle(12, 12, 3) + 'M4 4l16 16' },
  trash: { stroke: 'M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13M10 11v5M14 11v5' },
  download: { stroke: 'M12 4v11M7 10l5 5 5-5M5 20h14' },
  assign: { stroke: 'M4 12h11M11 7l5 5-5 5M20 5v14' },
  'chevron-left': { stroke: 'M15 6l-6 6 6 6' },
  'chevron-right': { stroke: 'M9 6l6 6-6 6' },
  'chevron-up': { stroke: 'M6 15l6-6 6 6' },
  'chevron-down': { stroke: 'M6 9l6 6 6-6' },
  'arrow-up': { stroke: 'M12 19V5M6 11l6-6 6 6' },
  'arrow-down': { stroke: 'M12 5v14M6 13l6 6 6-6' },
  external: {
    stroke: 'M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5',
  },
  play: { fill: 'M7 4.5v15l12-7.5z' },
  record: { fill: circle(12, 12, 6) },
  mic: {
    stroke: 'M9 6a3 3 0 0 1 6 0v5a3 3 0 0 1-6 0zM5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21M9 21h6',
  },
  music: { stroke: 'M9 18V5l11-2v13' + circle(6, 18, 3) + circle(17, 16, 3) },
  tab: { stroke: 'M5 4h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zM3 9h18' },
  clock: { stroke: circle(12, 12, 9) + 'M12 7v5l3 2' },
  /** Pinned: kept when randomizing. */
  pin: { stroke: 'M9 3h6l-1 6 4 4H6l4-4-1-6zM12 13v8' },
  'pin-filled': { stroke: 'M9 3h6l-1 6 4 4H6l4-4-1-6zM12 13v8', fill: 'M9 3h6l-1 6 4 4H6l4-4z' },
  book: { stroke: 'M4 19V5a2 2 0 0 1 2-2h14v14H6a2 2 0 0 0-2 2zm0 0a2 2 0 0 0 2 2h14v-4' },
  /** Oscillate: swing a value around where it's set. */
  wave: { stroke: 'M2 12c1.7-4.7 4.3-4.7 6.7 0s5 4.7 6.6 0 5-4.7 6.7 0' },
  /** Modulate: drive a value from the music. */
  modulate: { stroke: 'M4 10v4M8 6v12M12 9v6M16 4v16M20 10v4' },
  /** The Live pane: tempo and beat. */
  pulse: { stroke: 'M2 12h4l3-7 4 14 3-7h6' },
  /** The preset strip along the bottom. */
  strip: {
    stroke:
      'M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2zM3 15h18M9 15v6M15 15v6',
  },
  fullscreen: { stroke: 'M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5' },
  /** Performance mode: just the show on screen. */
  stage: {
    stroke: 'M4 4h16a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zM8 21h8M12 17v4',
    fill: 'M10 7.5v6l5-3z',
  },
} satisfies Record<string, { stroke?: string; fill?: string }>;

export type IconName = keyof typeof ICONS;
